<template>
  <PageContent>
    <Transition
      enter-from-class="translate-x-4 opacity-0"
      enter-to-class="translate-0 opacity-100"
      enter-active-class="transform transition duration-300 ease-linear"
      appear
    >
      <ProductDetailThreeColumnSkeleton
        v-if="fetching"
        :with-variant-box="true"
        :with-text-info-box="true"
        :with-attributes="true"
        :horizontal-layout="true"
      />
      <div v-else-if="product" :key="product.id">
        <ProductHeaderInfoNonRx :back-url="backUrl" :product="product" />
        <ProductDetailThreeColumns
          :product="product"
          :get-attribute="getProductAttribute(product)"
          :product-detail-properties="PRODUCT_DETAIL_ATTRIBUTE_KEYS_CL"
          :detail-name-mapping="detailNameMapping"
        >
          <template #actions>
            <div class="space-y-2 bg-white text-sm">
              <ProductPromotionGiftConditionBanner
                v-if="!areGiftConditionsMet && isGiftAddedToBasket"
                :product="product"
              />
              <ProductPromotionBanners v-else :product="product" />

              <div v-if="packageUnits && packageUnits.length">
                <div class="mb-2 text-xl font-medium">
                  {{ $t('pdp.package_type') }}
                </div>
                <div class="mb-8">
                  <div
                    class="top-row"
                    :class="{
                      'size-selection-row without-discount': !hasDiscount,
                      'size-selection-row with-discount': hasDiscount,
                    }"
                  >
                    <BaseText> {{ $t('pdp.lense_count_heading') }} </BaseText>
                    <BaseText> {{ $t('pdp.price_per_lense') }} </BaseText>
                    <BaseText v-if="hasDiscount">
                      <span>{{ $t('pdp.saving') }}*</span>
                    </BaseText>
                    <BaseText>{{ $t('pdp.price_heading') }}</BaseText>
                  </div>
                  <div
                    v-for="(unit, idx) in packageUnits"
                    :key="idx"
                    class="relative items-center text-[15px] lg:text-base"
                    :class="{
                      'size-selection-row without-discount': !hasDiscount,
                      'size-selection-row with-discount': hasDiscount,
                      'text-neutral-30': unit.isSoldOut,
                    }"
                  >
                    <FimRadio
                      v-if="packageUnits.length > 1"
                      v-model="activeProductId"
                      class="text-[15px] md:text-base"
                      :radio-value="unit.productId"
                      radio-name="package-units"
                      :label="unit.numberOfLenses.toString()"
                      data-testid="packaging-radio-button"
                      :disabled="unit.isSoldOut"
                      @update:model-value="changeUnit(unit)"
                    />
                    <div v-else class="text-[15px] md:text-base">
                      {{ unit.numberOfLenses }}
                    </div>
                    <div class="pt-0.5 text-[15px] md:text-base">
                      <span v-if="unit.pricePerUnit">
                        {{ toCurrency(unit.pricePerUnit) }}
                      </span>
                      <span v-else> – </span>
                    </div>
                    <div v-if="hasDiscount" class="w-auto font-semibold">
                      <span
                        v-if="unit.discount"
                        class="w-auto whitespace-nowrap rounded-full bg-red-500 px-2 py-0.5 text-[14px] font-medium tracking-wide text-white"
                      >
                        -
                        {{ unit.discount }}
                        %
                      </span>
                    </div>

                    <ProductPrice
                      class="text-[15px] font-normal"
                      :class="{ 'text-pricing': unit.discount }"
                      :price="unit.bapiPrice"
                      :quantity="1"
                      :applied-reductions="unit.bapiPrice.appliedReductions"
                      :show-price-reduction-badge="hasSpecial"
                      :align="'right'"
                      :break-line="true"
                    />
                    <span
                      v-if="unit.lowestPrice"
                      class="absolute bottom-1 right-0 text-xs text-gray-600"
                    >
                      {{ $t('pdp.lowest_price') }}
                      {{ `${unit.lowestPrice}` }}
                    </span>
                  </div>
                  <div
                    v-if="packageUnits.length > 1"
                    class="mt-5 text-gray-600 md:mt-3"
                  >
                    * {{ $t('pdp.package_discount_info') }}
                  </div>
                </div>
              </div>
              <div>
                <ProductDetailLensVariantSelection
                  v-model="selectedVariantsIds"
                  :product="product"
                  @update-error-state="hasErrorOnVariantSelection = $event"
                  @fetching-variant="fetchingVariants = $event"
                />
                <div v-if="!hasErrorOnVariantSelection" class="mt-4">
                  <div class="mb-2 text-sm font-medium">
                    {{ $t('pdp.packages_heading') }}
                  </div>
                  <div class="grid grid-cols-4 gap-x-4 md:gap-x-6 lg:gap-x-12">
                    <QuantityPicker
                      id="quantity-picker-left"
                      :value="quantity.left"
                      class="col-span-2"
                      :max-quantity="9"
                      :min-quantity="0"
                      @input="setLensQuantity({ quantityL: $event })"
                    />
                    <QuantityPicker
                      id="quantity-picker-right"
                      :value="quantity.right"
                      class="col-span-2"
                      :max-quantity="9"
                      :min-quantity="0"
                      @input="setLensQuantity({ quantityR: $event })"
                    />
                  </div>
                </div>
                <div
                  v-if="!hasErrorOnVariantSelection"
                  class="mt-9 text-xl font-medium"
                >
                  {{
                    $t('pdp.lenses.numberOfPackages', {
                      amountPackages: quantity.left + quantity.right,
                      numberOfLenses,
                    })
                  }}
                </div>
              </div>
              <ProductSubscription
                v-if="
                  isSubscriptionEnabled &&
                  isProductSubscriptionEligible({
                    variants: [variantLeft, variantRight],
                    attributes: product.attributes,
                  })
                "
                :product="product"
                :quantity="quantity.left + quantity.right"
                :variants="[variantLeft, variantRight]"
                :disabled="!allowA2B"
                class="mt-4"
                @add-item-to-basket="addItemToBasket($event)"
              >
                <template #price="slotProps">
                  <ProductPrice
                    size="md"
                    :price="slotProps.price || packagePrice"
                    :quantity="quantity.left + quantity.right"
                    :applied-reductions="
                      slotProps.price
                        ? slotProps.price?.appliedReductions
                        : packagePrice?.appliedReductions
                    "
                    :show-price-from="hasSpecial"
                    :show-price-reduction-badge="hasSpecial"
                    :show-discount="true"
                  />

                  <span v-if="lowestPrice" class="flex text-sm text-gray-600">
                    {{ $t('pdp.lowest_price') }}
                    {{ `${lowestPrice}` }}
                  </span>
                </template>
                <template #tax-extras="slotProps">
                  {{ $t('pdp.with_tax') }}
                  <ProductShippingCosts
                    :price="packagePrice"
                    :quantity="quantity.left + quantity.right"
                    :product-id="product.id"
                    :is-subscription="!!slotProps.subscription"
                  /><br />
                  {{ $t('pdp.delivery_location_information') }}
                </template>
                <template #delivery-estimates>
                  <ProductDetailDeliveryEstimates
                    v-if="showDeliveryEstimates"
                    :key="deliveryEstimatesKey"
                    :sales-status="salesStatus"
                    :stock="stock"
                    :product="product"
                    class="mt-3"
                  />
                </template>
                <template #recommendations>
                  <FimRecommendationStack
                    v-if="productRecommendations"
                    :recommendation-product="product"
                    :product-recommendations="productRecommendations"
                    class="mb-6"
                    :current-product-category="currentProductCategory"
                    location="PDP"
                  />
                </template>
              </ProductSubscription>
              <div
                v-else
                class="col-span-5 row-span-2 mt-4 lg:col-start-9 lg:col-end-13 lg:row-start-1"
              >
                <ProductPrice
                  class="mb-4"
                  size="2xl"
                  :price="packagePrice"
                  :quantity="quantity.left + quantity.right"
                  :applied-reductions="packagePrice?.appliedReductions"
                  :show-price-reduction-badge="hasSpecial"
                  :show-discount="true"
                />
                <span class="text-sm text-gray-600">
                  {{ $t('pdp.with_tax') }}
                  <ProductShippingCosts
                    :price="packagePrice"
                    :quantity="quantity.left + quantity.right"
                    :product-id="product.id"
                  /><br />
                  {{ $t('pdp.delivery_location_information') }}
                </span>
                <span v-if="lowestPrice" class="flex text-sm text-gray-600">
                  {{ $t('pdp.lowest_price') }}
                  {{ `${lowestPrice}` }}
                </span>
                <ProductDetailDeliveryEstimates
                  v-if="showDeliveryEstimates"
                  :key="deliveryEstimatesKey"
                  :sales-status="salesStatus"
                  :stock="stock"
                  :product="product"
                  class="mt-3"
                />
                <div class="my-6">
                  <div class="flex items-center space-x-2">
                    <FimButton
                      type="primary"
                      class="w-full"
                      :disabled="fetchingVariantIds || !allowA2B"
                      :loading="isAddingToCart"
                      data-testid="add-to-cart"
                      @click="() => addItemToBasket()"
                    >
                      {{ $t('pdp.add_label') }}
                      <template #iconAfter>
                        <LazyIconFielmannCart class="mr-2 size-6" />
                      </template>
                    </FimButton>
                    <WishlistToggle
                      :product="product"
                      :variant="variantLeft ?? variantRight"
                      layout="button"
                    />
                  </div>
                  <FadeInTransition>
                    <ProductPromotionGifts
                      v-if="isBuyXGetYPrioritized && !isGiftAddedToBasket"
                      :product="product"
                      class="mt-6"
                    />
                  </FadeInTransition>
                  <ProductShippingCostInformation class="max-w-2xs" />
                </div>
                <FimRecommendationStack
                  v-if="productRecommendations"
                  :recommendation-product="product"
                  :product-recommendations="productRecommendations"
                  class="mb-6"
                  :current-product-category="currentProductCategory"
                  location="PDP"
                />
              </div>
              <BenefitsList slug="contact-lenses" />
            </div>
          </template>
          <template #manuals>
            <div v-if="manualUrl || complianceUrl">
              <Headline class="mb-6" tag="span" look="h3">
                {{ $t('pdp.manual.headline') }}
              </Headline>
              <ProductDetailManualLink
                v-if="manualUrl"
                class="mb-6"
                :url="manualUrl"
                translation-key="pdp.manual.copyContactLens"
              />
              <ProductDetailManualLink
                v-if="complianceUrl"
                :url="complianceUrl"
                translation-key="pdp.manual.copyCompliance"
                link-translation-key="pdp.manual.linkCompliance"
              />
            </div>
          </template>
        </ProductDetailThreeColumns>
        <FimRecommendations
          v-if="recommendationRefXsell"
          :recommendation-product="product"
          :product-recommendations="recommendationRefXsell"
          :title="getRecommendationTitle('recommendationRefXsell', $i18n)"
          :current-product-category="currentProductCategory"
          class="mt-12"
          location="PDP"
        >
          <FimRecommendationLinks field-name="contact_lenses_links" />
        </FimRecommendations>
        <BazaarVoice :id="product.id" show="reviews" />
        <ClientOnly>
          <ProductDetailCartOverlay
            :recommendation-product="product"
            :product-recommendations="productRecommendations"
            :recommendation-attribute="
              PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_RELATED
            "
            :current-product-category="currentProductCategory"
          >
            <FimRecommendationLinks field-name="contact_lenses_links" />
          </ProductDetailCartOverlay>
        </ClientOnly>
      </div>
    </Transition>
  </PageContent>
</template>
<script setup lang="ts">
/* eslint sonarjs/cognitive-complexity: 1 */

import type { AddOrUpdateItemType, Product } from '@scayle/storefront-nuxt'
import type { BasketItemDisplayDataKey } from '@scayle/storefront-api'
import { ExistingItemHandling } from '@scayle/storefront-api'
import type { IAddOrUpdateItems } from '~/rpcMethods/basket'
import { useLensSelectionState } from '~/composables/pdp/useLensSelectionState'
import { useFimBasket } from '~/composables/useFimBasket'
import { useLenses } from '~/composables/pdp/useLenses'
import { useLensesVariants } from '~/composables/pdp/useLensesVariants'
import useUiState from '~/composables/ui/useUiState'
import { useToast } from '~/composables/useToast'
import {
  VARIANT_ID_LEFT_KEY_NAME,
  VARIANT_ID_RIGHT_KEY_NAME,
} from '~/constants/lens'
import {
  generateBreadcrumbSchema,
  getBreadcrumbSchemaItems,
  getProductHead,
  generateProductSchema,
} from '~/utils/seo'
import { Action } from '~/plugins/toast'
import { getCustomDataForBasket } from '~/utils/customData'
import {
  ProductCategories,
  getRecommendationTitle,
  getProductAttribute,
} from '~/utils/product'
import { getLowestPriorPrice, toCurrency } from '~/utils/price'
import {
  PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_RELATED,
  PRODUCT_DETAIL_ATTRIBUTE_KEYS_CL,
} from '~/constants/product'
import { PRODUCT_WITH_CL_DETAIL } from '~/constants/withParams'
import {
  eyeInputFullySelected,
  getContactLensDisplayDataForBasket,
  getContactLensValuesFromVariant,
  getDefaultSelectData,
  ContactLensDisplayDataAttributes,
} from '~/utils/contactLenses'
import type { LocationQuery } from '#vue-router'
const { logger } = useLogging()
const props = defineProps({
  baseProduct: {
    type: Object as PropType<Product>,
    required: true,
  },
})

onBeforeMount(() => {
  checkPackageUnitAvailability()
})

const activeProductId = ref(`${props.baseProduct.id}`)

const scope = effectScope()
onScopeDispose(() => scope.stop())
const context = useNuxtApp()
const { $log, $i18n, $alert, $featureToggle } = context
const $currentShop = useCurrentShop()
const { setPageState } = usePageState()
const route = useRoute()
const router = useRouter()

const { isSubscriptionEnabled } = $featureToggle
const hasErrorOnVariantSelection = ref(false)

const {
  backUrl,
  trackProductView,
  breadcrumbs,
  currentProductCategory,
  fetching,
  hasSpecial,
  manualUrl,
  complianceUrl,
  product: detailedProduct,
  salesStatus,
} = await useProductDetails(props.baseProduct, {
  params: {
    with: PRODUCT_WITH_CL_DETAIL,
  },
  options: { immediate: true },
})
const product = computed(() => detailedProduct.value || props.baseProduct)

const { data: shopCustomData } = useCustomData()

const {
  isBuyXGetYPrioritized,
  isGiftAddedToBasket,
  areGiftConditionsMet,
  highestPriorityPromotion,
} = useProductPromotions(product)

// Product recommendations
const { data: productRecommendations } = useAsyncProductRecommendations(
  [...PRODUCT_RECOMMENDATION_ATTRIBUTES],
  product.value,
)

const { trackPageView, trackAddToBasket, trackAddMultipleItemsToBasket } =
  useTrackingEvents()

const recommendationRefXsell = computed(
  () => productRecommendations.value?.recommendationRefXsell,
)

const head = computed(() =>
  getProductHead(
    product.value,
    undefined,
    product.value?.priceRange?.max,
    context,
  ),
)
useHead(() => head.value)
onMounted(() => {
  trackProductView(product.value)

  trackPageView({
    title: head.value.title,
    pageType: 'product',
  })
})

const { alert } = useToast()
const { addItem, addOrUpdateItems, items: basketItems } = useFimBasket()
const {
  checkedEyes,
  quantity,
  selectedVariantsIds,
  setLensQuantity,
  selectedVariantOptions,
  setLeftSelectedOption,
  setRightSelectedOption,
  isSelectedVariantIdSet,
} = useLensSelectionState()

const {
  availableAndInStock,
  combinedVariantStock: stock,
  hasSelectedVariant,
  numberOfLenses,
  packagePrice,
  packageUnits,
  variantLeft,
  variantRight,
  fetching: fetchingVariants,
  fetchVariants,
} = useLenses(product)

// The 'Ersparnis' column should only show if there aren't 'appliedReductions'
// and there are more than one packageUnit
const hasDiscount = computed(
  () =>
    packagePrice.value &&
    packagePrice.value.appliedReductions.length === 0 &&
    packageUnits.value.length > 1,
)

const {
  fetchingVariantIds,
  getSelectorValues,
  variantValues,
  fetchIdForSelections,
  leftEyeId,
  rightEyeId,
} = useLensesVariants(`useLensesVariants-${product.value.referenceKey}`)

const isAddingToCart = ref(false)

// todo: this was a hack to get around UI not updating if I remember correctly. Check if still needed
const deliveryEstimatesKey = ref(0)

const checkPackageUnitAvailability = () => {
  const isCurrentSoldOut = packageUnits.value.some(
    (unit) => unit.productId === +props.baseProduct.id && unit.isSoldOut,
  )
  if (!isCurrentSoldOut) {
    return
  }
  const nextAvailableUnit = packageUnits.value.find((unit) => !unit.isSoldOut)
  if (nextAvailableUnit) {
    const updatedFullPath = getUpdatedProductPath(nextAvailableUnit, route.path)
    router.replace({
      path: updatedFullPath,
      query: router.currentRoute.value.query,
    })
  }
}

const setSelectedOptions = async () => {
  if (isSelectedVariantIdSet('left') || isSelectedVariantIdSet('right')) {
    try {
      await fetchVariants(selectedVariantsIds.value)
      if (isSelectedVariantIdSet('left') && variantLeft.value) {
        setLeftSelectedOption(
          getContactLensValuesFromVariant(variantLeft.value),
        )
        if (isSelectedVariantIdSet('right') && variantRight.value) {
          setRightSelectedOption(
            getContactLensValuesFromVariant(variantRight.value),
          )
        }
      }
    } catch (e) {
      logger.error(e, {
        why: 'fetchVariants',
        where: 'contact-lens-pdp.vue',
        selectedVariantsIds: selectedVariantsIds.value,
      })
    }
  }
}

const changeUnit = async (packageUnit: any) => {
  const leftValue = route.query?.[VARIANT_ID_LEFT_KEY_NAME]
  const rightValue = route.query?.[VARIANT_ID_RIGHT_KEY_NAME]

  await Promise.all([
    fetchIdForSelections('left', packageUnit!.referenceKey),
    fetchIdForSelections('right', packageUnit!.referenceKey),
  ])

  const updatedFullPath = getUpdatedProductPath(packageUnit, route.path)

  const params = {
    ...(leftEyeId.value &&
      leftValue && { [VARIANT_ID_LEFT_KEY_NAME]: leftEyeId.value }),
    ...(rightEyeId.value &&
      rightValue && { [VARIANT_ID_RIGHT_KEY_NAME]: rightEyeId.value }),
  }

  router.replace({
    path: updatedFullPath,
    query: { ...route.query, ...(params as LocationQuery) },
  })
}

const getUpdatedProductPath = (packageUnit: any, slugOld: string) => {
  const splitted = slugOld?.split('-')
  // scayle-id prop in the slug
  splitted[splitted.length - 1] = packageUnit.productId.toString()
  // numberOfLenses prop in the slug
  splitted[splitted.length - 2] = packageUnit.numberOfLenses.toString()
  return splitted.join('-')
}

const { openBasketOverlay } = useUiState()

const isConsultantUsed = computed(
  () => getQueryParam(route.query, 'consultant', '0') === '1',
)

const addItemToBasket = async (
  basketItemSubscriptionAttributes?: MaybeRefOrGetter<BasketItemSubscriptionAttributes>,
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  if (
    fetchingVariantIds.value ||
    fetchingVariants.value ||
    isAddingToCart.value ||
    !Object.values(selectedVariantsIds.value).some(Boolean)
  ) {
    return
  }

  const subscriptionAttributes = toValue(basketItemSubscriptionAttributes)
  const isSameVariant =
    selectedVariantsIds.value.left === selectedVariantsIds.value.right
  const promotionId = highestPriorityPromotion.value?.id
  const branchId = getBranchIdFromSession()

  try {
    isAddingToCart.value = true

    if (
      isSubscriptionEnabled.value &&
      isSubscriptionAlreadyInBasket(
        !!subscriptionAttributes,
        parseInt(selectedVariantsIds.value.left, 10),
        basketItems.value,
      )
    ) {
      $alert.show(
        $i18n.t('basket.notification.subscription_already_in_basket_error', {
          productName: getProductName(product.value),
        }),
        Action.confirm,
      )
      return
    }

    if (isSameVariant) {
      const displayData = getContactLensDisplayDataForBasket(
        selectedVariantOptions,
        'both',
        $i18n,
        $currentShop.value,
        numberOfLenses.value,
        stock.value,
        shopCustomData.value,
      )
      const customData = {
        ...getCustomDataForBasket(
          selectedVariantOptions,
          'both',
          $currentShop.value,
          quantity.value,
          ProductCategories.contactLenses,
        ),
        ...(branchId && { branchId }),
        ...(isConsultantUsed.value && { consultantUsed: true }),
        designerOrder: isDesignerProduct(product),
        ...(subscriptionAttributes?.customData && {
          ...subscriptionAttributes.customData,
        }),
        consultantUsed: isConsultantUsed.value,
        productType: getProductType(product.value),
      }

      const addItemPayload: AddOrUpdateItemType = {
        variantId: variantLeft.value?.id ?? 0,
        quantity: quantity.value.left + quantity.value.right,
        displayData,
        customData,
        ...(!promotionId && { promotionId: null }),
        ...(promotionId && !isBuyXGetYPrioritized.value && { promotionId }),
      }

      // subscription displaydata for contactlenses shoule be added in slot 4
      if (subscriptionAttributes?.displayData) {
        addItemPayload.displayData = {
          ...addItemPayload.displayData,
          [ContactLensDisplayDataAttributes.deliveryForecastAttribute as BasketItemDisplayDataKey]:
            subscriptionAttributes.displayData['attribute-1'],
        }
      }

      await addItem(addItemPayload)
      trackAddToBasket(
        product.value,
        quantity.value.left + quantity.value.right,
        variantLeft.value,
        {
          overridePrice: (product: Product) => product.priceRange?.max,
          customData: addItemPayload.customData,
        },
      )
    } else {
      const items: IAddOrUpdateItems['items'] = []
      for (const [dir, enabled] of Object.entries(hasSelectedVariant.value)) {
        if (enabled) {
          const direction = dir as keyof typeof hasSelectedVariant.value
          const variant = direction === 'left' ? variantLeft : variantRight

          const displayData = getContactLensDisplayDataForBasket(
            selectedVariantOptions,
            direction,
            $i18n,
            $currentShop.value,
            numberOfLenses.value,
            stock.value,
            shopCustomData.value,
          )
          const customData = {
            ...getCustomDataForBasket(
              selectedVariantOptions,
              direction,
              $currentShop.value,
              quantity.value,
              ProductCategories.contactLenses,
            ),
            ...(branchId && { branchId }),
            ...(isConsultantUsed.value && { consultantUsed: true }),
            designerOrder: isDesignerProduct(product),
            ...(subscriptionAttributes?.customData && {
              ...subscriptionAttributes.customData,
            }),
          }
          const addItemPayload = {
            variantId: variant.value?.id ?? 0,
            quantity: quantity.value[direction],
            params: {
              displayData,
              customData,
              promotionId,
            },
          } satisfies IAddOrUpdateItems['items'][number]

          if (subscriptionAttributes?.displayData) {
            addItemPayload.params.displayData = {
              ...addItemPayload.params.displayData,
              [ContactLensDisplayDataAttributes.deliveryForecastAttribute as BasketItemDisplayDataKey]:
                subscriptionAttributes.displayData['attribute-1'],
            }
          }

          if (addItemPayload.quantity) {
            items.push(addItemPayload)
          }
        }
      }
      if (items.length) {
        await addOrUpdateItems(items, {
          existingItemHandling:
            ExistingItemHandling.ReplaceExistingWithCombinedQuantity,
        })

        const addToBasketPayload: AddToBasketTrackingPayload[] = items.map(
          (item) => {
            const variant = [variantLeft.value, variantRight.value].find(
              (variant) => variant?.id === item.variantId,
            )
            return {
              product: product.value,
              variant,
              quantity: item.quantity || 0,
              options: {
                overridePrice: (product: Product) => product.priceRange?.max,
                customData: item.params?.customData,
              },
            }
          },
        )
        trackAddMultipleItemsToBasket(addToBasketPayload)
      }
    }
    openBasketOverlay()
    isAddingToCart.value = false
  } catch (e: any) {
    isAddingToCart.value = false
    $log.error(e)
    alert($i18n.t('pdp.basket.error'), Action.confirm)
  }
}

const allowA2B = computed(() => {
  if (
    checkedEyes.value.includes('left') &&
    checkedEyes.value.includes('right')
  ) {
    return availableAndInStock.value.left && availableAndInStock.value.right
  }

  if (checkedEyes.value.includes('left')) {
    return availableAndInStock.value.left
  }

  if (checkedEyes.value.includes('right')) {
    return availableAndInStock.value.right
  }

  return false
})

const detailNameMapping = {
  name: $i18n.t('pdp.details.name'),
}

const lowestPrice = computed(() =>
  getLowestPriorPrice(
    product.value?.priceRange?.max,
    product.value?.lowestPriorPrice,
    quantity.value.left + quantity.value.right,
  ),
)

const breadcrumbSchema = computed(() =>
  getBreadcrumbSchemaItems(
    breadcrumbs.value,
    $currentShop.value?.domain ?? '',
    route.fullPath,
    head.value.title || '',
  ),
)

setPageState('pageType', 'product')
setPageState('productCategory', ProductCategories.contactLenses)

// categories: rootCategories,

const productSchema = computed(() =>
  generateProductSchema(
    product.value,
    undefined,
    product.value?.priceRange?.max,
    context,
  ),
)

const showDeliveryEstimates = ref(false)

useJsonld(() => productSchema.value)
useJsonld(() => generateBreadcrumbSchema(breadcrumbSchema.value))

scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch(stock, () => {
    deliveryEstimatesKey.value += 1
  })
  watch(selectedVariantsIds, () => {
    setSelectedOptions()
  })

  watch(selectedVariantOptions, (newValue) => {
    const value = toRaw(newValue)

    const checked =
      checkedEyes.value.includes('left') || checkedEyes.value.includes('right')
    const leftValid =
      !checkedEyes.value.includes('left') || eyeInputFullySelected(value.left)
    const rightValid =
      !checkedEyes.value.includes('right') || eyeInputFullySelected(value.right)

    showDeliveryEstimates.value =
      checked && leftValid && rightValid && Boolean(stock.value)
  })

  watch(
    () => packageUnits.value,
    () => {
      checkPackageUnitAvailability()
    },
  )

  watch(
    product,
    async (newValue, prevVal) => {
      if (newValue?.referenceKey && !prevVal) {
        const isAnyVariantSet =
          isSelectedVariantIdSet('left') || isSelectedVariantIdSet('right')
        const fetchers = [
          getSelectorValues(newValue.referenceKey),
          isAnyVariantSet && fetchVariants(selectedVariantsIds.value),
        ].filter(Boolean)
        try {
          await Promise.allSettled(fetchers)
          setLeftSelectedOption(
            isSelectedVariantIdSet('left') && variantLeft.value
              ? getContactLensValuesFromVariant(variantLeft.value)
              : getDefaultSelectData(variantValues.value),
          )
          setRightSelectedOption(
            isSelectedVariantIdSet('right') && variantRight.value
              ? getContactLensValuesFromVariant(variantRight.value)
              : getDefaultSelectData(variantValues.value),
          )
        } catch (e) {
          logger.error(e, {
            why: 'getSelectorValues',
            where: 'contact-lens-pdp.vue',
            id: newValue.referenceKey,
          })
        }
      }
    },
    { immediate: true },
  )

  watch(allowA2B, () => {
    deliveryEstimatesKey.value += 1
  })
})
</script>
